import { useEffect } from "react";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { marketApi } from "../market/api";
import { useAsync } from "../lib/useAsync";

const BENEFITS: { icon: string; title: MessageKey; text: MessageKey }[] = [
  { icon: "💸", title: "sell.b1Title", text: "sell.b1Text" },
  { icon: "🏦", title: "sell.b2Title", text: "sell.b2Text" },
  { icon: "🔎", title: "sell.b3Title", text: "sell.b3Text" },
  { icon: "🏪", title: "sell.b4Title", text: "sell.b4Text" },
  { icon: "📦", title: "sell.b5Title", text: "sell.b5Text" },
  { icon: "⚡", title: "sell.b6Title", text: "sell.b6Text" },
];
/** Texts with a version that mentions Mercado Pago (key + "Mp"). */
const MP_VARIANTS = new Set<MessageKey>(["sell.b2Text", "sell.a1", "sell.a2"]);
const STEPS: { title: MessageKey; text: MessageKey }[] = [
  { title: "sell.s1Title", text: "sell.s1Text" },
  { title: "sell.s2Title", text: "sell.s2Text" },
  { title: "sell.s3Title", text: "sell.s3Text" },
];
const FAQ: { q: MessageKey; a: MessageKey }[] = [
  { q: "sell.q1", a: "sell.a1" },
  { q: "sell.q2", a: "sell.a2" },
  { q: "sell.q3", a: "sell.a3" },
  { q: "sell.q4", a: "sell.a4" },
  { q: "sell.q5", a: "sell.a5" },
  { q: "sell.q6", a: "sell.a6" },
];

/** /vender — why sell on pokefans.fun (no fees, paid directly, the store's traffic) and how to start. */
export function SellPage() {
  const { t } = useI18n();
  const auth = useAuth();
  const info = useAsync((signal) => marketApi.info(signal), []);
  const left = info.data?.foundersLeft ?? 0;
  // Mercado Pago is only promised once sellers can actually connect it.
  const mp = info.data?.mpEnabled === true;
  const withMp = (key: MessageKey): MessageKey => (mp && MP_VARIANTS.has(key) ? (`${key}Mp` as MessageKey) : key);
  // Signed in: straight to the request; otherwise create the account first and come back to it.
  const start = auth.user ? "/vender/solicitar" : `/registro?next=${encodeURIComponent("/vender/solicitar")}`;

  useEffect(() => {
    document.title = `${t("sell.metaTitle")} | pokefans.fun`;
  }, [t]);

  const cta = (
    <div className="btn-row">
      <Link to={start} className="btn btn-accent btn-lg">
        {t("sell.cta")}
      </Link>
      {!auth.user && (
        <Link to={`/ingresar?next=${encodeURIComponent("/vender/solicitar")}`} className="btn btn-link">
          {t("sell.haveAccount")}
        </Link>
      )}
    </div>
  );

  return (
    <div className="sell-page">
      <section className="sell-hero">
        <div className="container sell-hero-inner">
          <p className="sell-kicker">{t("sell.kicker")}</p>
          <h1>{t("sell.title")}</h1>
          <p className="sell-lead">{t("sell.lead")}</p>
          {cta}
        </div>
      </section>

      {left > 0 && (
        <section className="container sell-founders" aria-labelledby="founders-title">
          <span className="sell-founders-badge" aria-hidden="true">
            🏛️
          </span>
          <div>
            <h2 id="founders-title">{t("sell.foundersTitle")}</h2>
            <p>{t("sell.foundersText")}</p>
            <p className="sell-founders-left">{t("sell.foundersLeft", { count: left })}</p>
          </div>
        </section>
      )}

      <section className="container section" aria-labelledby="benefits-title">
        <h2 id="benefits-title" className="section-title">
          {t("sell.benefitsTitle")}
        </h2>
        <ul className="sell-benefits">
          {BENEFITS.map((b) => (
            <li key={b.title}>
              <span className="sell-benefit-icon" aria-hidden="true">
                {b.icon}
              </span>
              <h3>{t(b.title)}</h3>
              <p>{t(withMp(b.text))}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="container section" aria-labelledby="steps-title">
        <h2 id="steps-title" className="section-title">
          {t("sell.stepsTitle")}
        </h2>
        <ol className="sell-steps">
          {STEPS.map((s, i) => (
            <li key={s.title}>
              <span className="sell-step-n" aria-hidden="true">
                {i + 1}
              </span>
              <div>
                <h3>{t(s.title)}</h3>
                <p>{t(s.text)}</p>
              </div>
            </li>
          ))}
        </ol>
        {cta}
        <p className="muted">
          {t("plans.fromSell")} <Link to="/vender/planes">{t("plans.see")} →</Link>
        </p>
        <p className="muted">
          {t("buy.fromSell")} <Link to="/compramos">{t("nav.buy")} →</Link>
        </p>
      </section>

      <section className="container section" aria-labelledby="faq-title">
        <h2 id="faq-title" className="section-title">
          {t("sell.faqTitle")}
        </h2>
        <div className="sell-faq">
          {FAQ.map((f) => (
            <details key={f.q}>
              <summary>{t(f.q)}</summary>
              <p>{t(withMp(f.a))}</p>
            </details>
          ))}
        </div>
        <p className="muted small">
          {t("sell.termsNote")} <Link to="/mercado/terminos">{t("sell.terms")}</Link>
        </p>
      </section>
    </div>
  );
}
